import type { CSSProperties } from 'react';
import { createTheme, ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    table: {
      divider: string;
      headerBackground: string;
      summaryBackground: string;
    };
  }

  interface PaletteOptions {
    table?: {
      divider?: string;
      headerBackground?: string;
      summaryBackground?: string;
    };
  }

  interface TypographyVariants {
    'label-small': CSSProperties;
    'body-small': CSSProperties;
  }

  interface TypographyVariantsOptions {
    'label-small'?: CSSProperties;
    'body-small'?: CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    'label-small': true;
    'body-small': true;
  }
}

/**
 * The base AEG design-system colour palette and shape tokens.
 * Consumers can extend this via `createAEGTheme()`.
 */
const baseThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: '#052E54',
      light: '#375876',
      dark: '#03172A',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D41034',
      light: 'rgba(5, 46, 84, 0.3)',
      dark: 'rgba(5, 46, 84, 0.1)',
      contrastText: '#FFFFFF',
    },
    info: {
      main: '#0288D1',
      light: '#03A9F4',
      dark: '#01579B',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F5F5F5',
      paper: '#FFFFFF',
    },
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
    },
    divider: 'rgba(0, 0, 0, 0.23)',
    table: {
      divider: '#0000001F',
      headerBackground: 'rgb(245, 247, 249)', // equivalent to rgba(5, 46, 84, 0.04) in a white background
      summaryBackground: '#D3D5DB',
    },
  },
  typography: {
    fontFamily: ['Roboto', '-apple-system', 'BlinkMacSystemFont', '"Segoe UI"', 'Arial', 'sans-serif'].join(','),
    'label-small': {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '16px',
      letterSpacing: '0.1px',
    },
    'body-small': {
      fontFamily: 'Roboto',
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: '20px',
      letterSpacing: '0.1px',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F5F5',
        },
        a: {
          color: 'inherit',
          textDecoration: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
  shape: {
    borderRadius: 4,
  },
};

/**
 * A pre-built MUI theme instance using the AEG base palette.
 * Use this when you don't need any customisation.
 *
 * @example
 * ```tsx
 * import { aegTheme } from '@aeg-presents/shared-components/aeg-theme';
 * <ThemeProvider theme={aegTheme}>…</ThemeProvider>
 * ```
 */
export const aegTheme = createTheme(baseThemeOptions);

/**
 * Creates an AEG-branded MUI theme with optional consumer overrides
 * deep-merged on top of the base palette/typography tokens.
 *
 * @param overrides - Any valid `ThemeOptions` to merge on top of the AEG base.
 * @returns A fully resolved MUI `Theme`.
 *
 * @example
 * ```tsx
 * import { createAEGTheme } from '@aeg-presents/shared-components/aeg-theme';
 *
 * const appTheme = createAEGTheme({
 *   palette: { primary: { main: '#003366' } },
 * });
 * <ThemeProvider theme={appTheme}>…</ThemeProvider>
 * ```
 */
export function createAEGTheme(overrides: ThemeOptions = {}): ReturnType<typeof createTheme> {
  return createTheme(baseThemeOptions, overrides);
}
